import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  Download,
  Search,
  Eye,
  CreditCard,
  Building2,
  Star,
  PlusCircle,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HoldingFinances() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [teamPayments, setTeamPayments] = useState([]);
  const [workerEarnings, setWorkerEarnings] = useState([]);
  
  // Search states
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [paymentSearch, setPaymentSearch] = useState("");
  const [workerSearch, setWorkerSearch] = useState("");
  
  // Modal states
  const [markPaidModal, setMarkPaidModal] = useState({ open: false, type: null, item: null });
  const [createInvoiceModal, setCreateInvoiceModal] = useState({ open: false, item: null });
  const [workerDetailModal, setWorkerDetailModal] = useState({ open: false, worker: null });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (token) {
      fetchAllData();
    }
  }, [token]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchOverview(),
      fetchInvoices(),
      fetchTeamPayments(),
      fetchWorkerEarnings()
    ]);
    setLoading(false);
  };

  const fetchOverview = async () => {
    try {
      const res = await fetch(`${API_URL}/api/holding/finances/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOverview(data);
      }
    } catch (err) {
      console.error("Error fetching overview:", err);
    }
  };

  const fetchInvoices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/holding/finances/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data);
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
    }
  };

  const fetchTeamPayments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/holding/finances/team-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTeamPayments(data);
      }
    } catch (err) {
      console.error("Error fetching team payments:", err);
    }
  };

  const fetchWorkerEarnings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/holding/finances/worker-earnings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWorkerEarnings(data);
      }
    } catch (err) {
      console.error("Error fetching worker earnings:", err);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0 kr";
    return new Intl.NumberFormat('nb-NO', { style: 'decimal' }).format(amount) + " kr";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const handleCreateInvoice = async () => {
    if (!createInvoiceModal.item) return;
    setProcessing(true);
    
    try {
      const res = await fetch(`${API_URL}/api/holding/finances/invoices/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          project_id: createInvoiceModal.item.project_id,
          due_days: 14
        })
      });
      
      if (res.ok) {
        toast.success(t('finances.invoiceCreated'));
        setCreateInvoiceModal({ open: false, item: null });
        fetchInvoices();
      } else {
        const err = await res.json();
        toast.error(err.detail || t('common.error'));
      }
    } catch (err) {
      toast.error(t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!markPaidModal.item) return;
    setProcessing(true);
    
    try {
      const endpoint = markPaidModal.type === "invoice"
        ? `${API_URL}/api/holding/finances/invoices/${markPaidModal.item.invoice_id}/mark-paid`
        : `${API_URL}/api/holding/finances/team-payments/${markPaidModal.item.project_id}/mark-paid`;
      
      const res = await fetch(endpoint, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({})
      });
      
      if (res.ok) {
        toast.success(t('finances.markedAsPaid'));
        setMarkPaidModal({ open: false, type: null, item: null });
        if (markPaidModal.type === "invoice") {
          fetchInvoices();
        } else {
          fetchTeamPayments();
        }
        fetchOverview();
      } else {
        const err = await res.json();
        toast.error(err.detail || t('common.error'));
      }
    } catch (err) {
      toast.error(t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadInvoice = async (projectId) => {
    try {
      toast.info(t('finances.downloadInvoice'));
      const res = await fetch(`${API_URL}/api/holding/finances/invoices/${projectId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Could not download");
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `faktura_${projectId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(t('finances.invoiceDownloaded'));
    } catch (err) {
      toast.error(t('common.error'));
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      paid: { label: t('finances.paid'), color: "bg-green-500/20 text-green-400", icon: CheckCircle2 },
      pending: { label: t('status.pending'), color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
      overdue: { label: t('finances.overdue'), color: "bg-red-500/20 text-red-400", icon: AlertCircle },
      not_invoiced: { label: t('finances.notInvoiced'), color: "bg-gray-500/20 text-gray-400", icon: FileText },
      pending_signature: { label: t('finances.pendingSignature'), color: "bg-blue-500/20 text-blue-400", icon: FileText },
      not_paid: { label: t('finances.notPaid'), color: "bg-orange-500/20 text-orange-400", icon: CreditCard },
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border-0`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Filtered data
  const filteredInvoices = invoices.filter(inv => 
    inv.project_title?.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
    inv.client_company?.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
    inv.invoice_number?.toLowerCase().includes(invoiceSearch.toLowerCase())
  );

  const filteredPayments = teamPayments.filter(pay =>
    pay.project_title?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
    pay.team_leader_name?.toLowerCase().includes(paymentSearch.toLowerCase())
  );

  const filteredWorkers = workerEarnings.filter(w =>
    w.name?.toLowerCase().includes(workerSearch.toLowerCase()) ||
    w.worker_code?.toLowerCase().includes(workerSearch.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white">{t('finances.title')}</h1>
          <p className="text-gray-400 mt-1">{t('finances.subtitle')}</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#1a1a2e] border border-[#2a2a4a]">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black">
              {t('finances.overview')}
            </TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black">
              {t('finances.invoices')} ({invoices.length})
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black">
              {t('finances.teamPayments')} ({teamPayments.length})
            </TabsTrigger>
            <TabsTrigger value="workers" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black">
              {t('finances.workerEarnings')} ({workerEarnings.length})
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">{t('finances.totalIncome')}</p>
                      <p className="text-2xl font-bold text-green-400">
                        {formatCurrency(overview?.summary?.total_income)}
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        {overview?.summary?.contracts_count || 0} {t('finances.contracts')}
                      </p>
                    </div>
                    <div className="p-3 bg-green-500/20 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-green-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/20">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">{t('finances.totalExpenses')}</p>
                      <p className="text-2xl font-bold text-red-400">
                        {formatCurrency(overview?.summary?.total_team_payments)}
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        {overview?.summary?.projects_with_teams || 0} {t('finances.projects')}
                      </p>
                    </div>
                    <div className="p-3 bg-red-500/20 rounded-lg">
                      <TrendingDown className="w-6 h-6 text-red-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">{t('finances.platformFee')} ({overview?.fees?.platform_fee_percent}%)</p>
                      <p className="text-2xl font-bold text-blue-400">
                        {formatCurrency(overview?.summary?.total_platform_fees)}
                      </p>
                    </div>
                    <div className="p-3 bg-blue-500/20 rounded-lg">
                      <DollarSign className="w-6 h-6 text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">{t('finances.grossProfit')}</p>
                      <p className="text-2xl font-bold text-purple-400">
                        {formatCurrency(overview?.summary?.gross_profit)}
                      </p>
                    </div>
                    <div className="p-3 bg-purple-500/20 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Monthly Chart (Simple bar representation) */}
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader>
                <CardTitle className="text-white">{t('finances.monthlyOverview')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {overview?.monthly_breakdown?.map((month, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">{month.month}</span>
                        <span className="text-white">
                          {t('finances.income')}: {formatCurrency(month.income)} | 
                          {t('finances.expenses')}: {formatCurrency(month.expenses)} | 
                          <span className={month.profit >= 0 ? "text-green-400" : "text-red-400"}>
                            {" "}{t('finances.profit')}: {formatCurrency(month.profit)}
                          </span>
                        </span>
                      </div>
                      <div className="flex gap-2 h-4">
                        <div 
                          className="bg-green-500/40 rounded"
                          style={{ width: `${Math.min((month.income / (overview?.summary?.total_income || 1)) * 100 * 6, 100)}%` }}
                        />
                        <div 
                          className="bg-red-500/40 rounded"
                          style={{ width: `${Math.min((month.expenses / (overview?.summary?.total_team_payments || 1)) * 100 * 6, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* INVOICES TAB */}
          <TabsContent value="invoices" className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  placeholder={t('finances.searchProjectCustomer')}
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  className="pl-10 bg-[#1a1a2e] border-[#2a2a4a] text-white"
                />
              </div>
            </div>

            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#2a2a4a]">
                      <TableHead className="text-gray-400">{t('finances.project')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.customer')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.invoiceNumber')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.amountInclVat')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.dueDate')}</TableHead>
                      <TableHead className="text-gray-400">{t('common.status')}</TableHead>
                      <TableHead className="text-gray-400 text-right">{t('finances.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.map((inv) => (
                      <TableRow key={inv.contract_id} className="border-[#2a2a4a]">
                        <TableCell className="text-white font-medium">{inv.project_title}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-300">{inv.client_company}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-400 font-mono">
                          {inv.invoice_number || "-"}
                        </TableCell>
                        <TableCell className="text-white font-medium">
                          {formatCurrency(inv.total_amount)}
                        </TableCell>
                        <TableCell className="text-gray-400">
                          {formatDate(inv.due_date)}
                        </TableCell>
                        <TableCell>{getStatusBadge(inv.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {inv.status === "not_invoiced" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setCreateInvoiceModal({ open: true, item: inv })}
                                className="text-blue-400 hover:text-blue-300"
                              >
                                <PlusCircle className="w-4 h-4 mr-1" />
                                {t('finances.create')}
                              </Button>
                            )}
                            {inv.invoice_id && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDownloadInvoice(inv.project_id)}
                                  className="text-[#23F7DD] hover:text-[#70FFEF]"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                {inv.status === "pending" && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setMarkPaidModal({ open: true, type: "invoice", item: inv })}
                                    className="text-green-400 hover:text-green-300"
                                  >
                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                    {t('finances.paid')}
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TEAM PAYMENTS TAB */}
          <TabsContent value="payments" className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  placeholder={t('finances.searchProjectTeamLeader')}
                  value={paymentSearch}
                  onChange={(e) => setPaymentSearch(e.target.value)}
                  className="pl-10 bg-[#1a1a2e] border-[#2a2a4a] text-white"
                />
              </div>
            </div>

            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#2a2a4a]">
                      <TableHead className="text-gray-400">{t('finances.project')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.teamLeader')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.teamSize')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.totalPayment')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.signed')}</TableHead>
                      <TableHead className="text-gray-400">{t('common.status')}</TableHead>
                      <TableHead className="text-gray-400 text-right">{t('finances.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayments.map((pay) => (
                      <TableRow key={pay.project_id} className="border-[#2a2a4a]">
                        <TableCell className="text-white font-medium">{pay.project_title}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-purple-400" />
                            <div>
                              <span className="text-gray-300">{pay.team_leader_name}</span>
                              {pay.team_leader_code && (
                                <Badge className="ml-2 bg-purple-500/20 text-purple-400 border-0 text-xs">
                                  {pay.team_leader_code}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-400">
                          {pay.team_size} {t('finances.persons')}
                        </TableCell>
                        <TableCell className="text-white font-medium">
                          {formatCurrency(pay.total_amount)}
                        </TableCell>
                        <TableCell className="text-gray-400">
                          {formatDate(pay.signed_at)}
                        </TableCell>
                        <TableCell>{getStatusBadge(pay.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setWorkerDetailModal({ open: true, worker: pay })}
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              {t('finances.details')}
                            </Button>
                            {pay.status === "not_paid" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setMarkPaidModal({ open: true, type: "payment", item: pay })}
                                className="text-green-400 hover:text-green-300"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                {t('finances.paid')}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* WORKER EARNINGS TAB */}
          <TabsContent value="workers" className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  placeholder={t('finances.searchNameCode')}
                  value={workerSearch}
                  onChange={(e) => setWorkerSearch(e.target.value)}
                  className="pl-10 bg-[#1a1a2e] border-[#2a2a4a] text-white"
                />
              </div>
            </div>

            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#2a2a4a]">
                      <TableHead className="text-gray-400">{t('finances.employee')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.code')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.role')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.rating')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.projectsCount')}</TableHead>
                      <TableHead className="text-gray-400">{t('finances.totalEarned')}</TableHead>
                      <TableHead className="text-gray-400 text-right">{t('finances.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredWorkers.map((worker) => (
                      <TableRow key={worker.user_id} className="border-[#2a2a4a]">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                              <Users className="w-4 h-4 text-blue-400" />
                            </div>
                            <div>
                              <span className="text-white font-medium">{worker.name}</span>
                              <p className="text-gray-500 text-xs">{worker.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-blue-500/20 text-blue-400 border-0">
                            {worker.worker_code || "-"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-400 capitalize">
                          {worker.role === "team_leader" ? t('roles.teamLeader') : t('roles.worker')}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {worker.rating > 0 ? (
                              <>
                                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                <span className="text-yellow-400">{worker.rating.toFixed(1)}</span>
                              </>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-400">
                          {worker.projects_count} {t('finances.projects')}
                        </TableCell>
                        <TableCell className="text-green-400 font-bold">
                          {formatCurrency(worker.total_earned)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setWorkerDetailModal({ open: true, worker: worker })}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {t('finances.seeProjects')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Invoice Modal */}
        <Dialog open={createInvoiceModal.open} onOpenChange={(open) => setCreateInvoiceModal({ open, item: null })}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white">
            <DialogHeader>
              <DialogTitle className="text-white">{t('finances.createInvoice')}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-400">
                {t('finances.createInvoiceConfirm')}{" "}
                <span className="text-white font-medium">{createInvoiceModal.item?.project_title}</span>?
              </p>
              <div className="mt-4 p-4 bg-[#0d0d1a] rounded-lg">
                <p className="text-gray-400">{t('finances.customer')}: <span className="text-white">{createInvoiceModal.item?.client_company}</span></p>
                <p className="text-gray-400">{t('finances.amount')}: <span className="text-green-400 font-bold">{formatCurrency(createInvoiceModal.item?.total_amount)}</span></p>
                <p className="text-gray-400">{t('finances.dueDate')}: <span className="text-white">14 {t('common.days')}</span></p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateInvoiceModal({ open: false, item: null })} className="border-[#2a2a4a]">
                {t('common.cancel')}
              </Button>
              <Button onClick={handleCreateInvoice} disabled={processing} className="bg-[#23F7DD] text-black hover:bg-[#70FFEF]">
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlusCircle className="w-4 h-4 mr-2" />}
                {t('finances.createInvoice')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark as Paid Modal */}
        <Dialog open={markPaidModal.open} onOpenChange={(open) => setMarkPaidModal({ open, type: null, item: null })}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white">
            <DialogHeader>
              <DialogTitle className="text-white">{t('finances.markAsPaid')}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-400">
                {t('finances.confirmPayment')} - {" "}
                <span className="text-white font-medium">{markPaidModal.item?.project_title}</span>
              </p>
              <div className="mt-4 p-4 bg-[#0d0d1a] rounded-lg">
                <p className="text-gray-400">
                  {t('finances.amount')}: <span className="text-green-400 font-bold">{formatCurrency(markPaidModal.item?.total_amount)}</span>
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMarkPaidModal({ open: false, type: null, item: null })} className="border-[#2a2a4a]">
                {t('common.cancel')}
              </Button>
              <Button onClick={handleMarkAsPaid} disabled={processing} className="bg-green-500 text-white hover:bg-green-600">
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {t('finances.confirmPayment')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Worker/Payment Detail Modal */}
        <Dialog open={workerDetailModal.open} onOpenChange={(open) => setWorkerDetailModal({ open, worker: null })}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                {workerDetailModal.worker?.name || workerDetailModal.worker?.team_leader_name}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {/* Team Payment breakdown */}
              {workerDetailModal.worker?.team_members && (
                <div className="space-y-3">
                  <h4 className="text-gray-400 font-medium">{t('finances.paymentBreakdown')}:</h4>
                  <div className="p-3 bg-[#0d0d1a] rounded-lg flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-500/20 text-purple-400">TL</Badge>
                      <span className="text-white">{workerDetailModal.worker?.team_leader_name}</span>
                    </div>
                    <span className="text-green-400 font-bold">{formatCurrency(workerDetailModal.worker?.team_leader_amount)}</span>
                  </div>
                  {workerDetailModal.worker?.team_members?.map((member, idx) => (
                    <div key={idx} className="p-3 bg-[#0d0d1a] rounded-lg flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-500/20 text-blue-400">{member.worker_code || "WRK"}</Badge>
                        <span className="text-white">{member.name}</span>
                      </div>
                      <span className="text-green-400 font-bold">{formatCurrency(member.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Worker projects list */}
              {workerDetailModal.worker?.projects && (
                <div className="space-y-3">
                  <h4 className="text-gray-400 font-medium">{t('finances.projectHistory')}:</h4>
                  {workerDetailModal.worker?.projects?.map((proj, idx) => (
                    <div key={idx} className="p-3 bg-[#0d0d1a] rounded-lg flex justify-between items-center">
                      <div>
                        <span className="text-white font-medium">{proj.title}</span>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={proj.role === "Team Leader" ? "bg-purple-500/20 text-purple-400" : "bg-blue-500/20 text-blue-400"}>
                            {proj.role === "Team Leader" ? t('roles.teamLeader') : t('roles.worker')}
                          </Badge>
                          <span className="text-gray-500 text-xs">{formatDate(proj.signed_at)}</span>
                        </div>
                      </div>
                      <span className="text-green-400 font-bold">{formatCurrency(proj.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
